import { useCallback, useRef, useState } from 'react'

import PlayButton from '@components/icons/PlayButton'

export interface VideoCardProps {
	videoUrl: string
	thumbnailUrl: string
	thumbnailAlt: string
	overlayText: string
	activeVideoRef: React.MutableRefObject<HTMLVideoElement | null>
}

export default function VideoCard({
	videoUrl,
	thumbnailUrl,
	thumbnailAlt,
	overlayText,
	activeVideoRef
}: VideoCardProps) {
	const videoRef = useRef<HTMLVideoElement>(null)
	const [isPlaying, setIsPlaying] = useState(false)
	const [isMuted, setIsMuted] = useState(false)

	const handlePlayPause = useCallback(() => {
		const video = videoRef.current
		if (!video) return

		if (!video.paused) {
			video.pause()
			return
		}

		// Pause any other playing video
		if (activeVideoRef.current && activeVideoRef.current !== video) {
			activeVideoRef.current.pause()
		}
		// eslint-disable-next-line no-param-reassign
		activeVideoRef.current = video
		// Silent play failures (e.g. policy) are rare on direct clicks; muted fallback helps iOS/Safari
		const tryPlay = () => video.play()
		tryPlay().catch(() => {
			video.muted = true
			setIsMuted(true)
			tryPlay().catch(() => {
				/* ignore */
			})
		})
	}, [activeVideoRef])

	const handleToggleMute = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			const video = videoRef.current
			if (!video) return
			video.muted = !isMuted
			setIsMuted(!isMuted)
		},
		[isMuted]
	)

	return (
		<div className="relative aspect-[3/5] rounded-lg overflow-hidden bg-gray-900">
			{/* Let horizontal swipes reach Swiper — native video hijacks touches otherwise */}
			<video
				ref={videoRef}
				src={videoUrl}
				poster={thumbnailUrl}
				aria-label={thumbnailAlt || overlayText || 'Video'}
				playsInline
				muted={isMuted}
				preload="none"
				className="pointer-events-none absolute inset-0 h-full w-full object-cover"
				onPlay={() => setIsPlaying(true)}
				onPause={() => setIsPlaying(false)}
				onEnded={() => setIsPlaying(false)}
			/>

			{/*
			  Full-card swiper-no-swiping broke mobile swipe (entire slide ignored drag).
			  Only the play control opts out of swiping; the rest of the card passes touch to Swiper.
			*/}
			<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
				<button
					type="button"
					className="swiper-no-swiping pointer-events-auto flex min-h-14 min-w-14 cursor-pointer touch-manipulation items-center justify-center rounded-full p-2"
					onClick={handlePlayPause}
					aria-label={isPlaying ? `Pause video: ${overlayText}` : `Play video: ${overlayText}`}
				>
					{!isPlaying && <PlayButton className="h-14 w-14 fill-white/90 drop-shadow-lg" />}
				</button>
			</div>

			{/* Mute/Unmute button */}
			{isPlaying && (
				<button
					type="button"
					className="swiper-no-swiping absolute top-3 right-3 z-20 flex h-8 w-8 touch-manipulation items-center justify-center rounded-full bg-black/50 text-white"
					onClick={handleToggleMute}
					aria-label={isMuted ? 'Unmute video' : 'Mute video'}
				>
					{isMuted ? (
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="currentColor"
							className="w-4 h-4"
						>
							<path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM17.78 9.22a.75.75 0 10-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 001.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 101.06-1.06L20.56 12l1.72-1.72a.75.75 0 00-1.06-1.06l-1.72 1.72-1.72-1.72z" />
						</svg>
					) : (
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="currentColor"
							className="w-4 h-4"
						>
							<path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.584 5.106a.75.75 0 011.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 01-1.06-1.06 8.25 8.25 0 000-11.668.75.75 0 010-1.06z" />
							<path d="M15.932 7.757a.75.75 0 011.061 0 6 6 0 010 8.486.75.75 0 01-1.06-1.061 4.5 4.5 0 000-6.364.75.75 0 010-1.06z" />
						</svg>
					)}
				</button>
			)}

			{/* Text overlay at bottom (hidden while video is playing) */}
			{!isPlaying && (
				<div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none p-2">
					<div className="bg-white/80 backdrop-blur-sm rounded-md py-3 px-3">
						<p className="text-gray-900 text-lg font-sans leading-snug">{overlayText}</p>
					</div>
				</div>
			)}
		</div>
	)
}
